import { Loader2, TriangleAlert } from "lucide-react";
import { createContext, useContext } from "react";
import { useTranslation } from "react-i18next";
import "twin.macro";

import { useRainfallData } from "@/api/climate-data/queries";
import { ClimateDataGranularity, RainfallData } from "@/api/climate-data/types";
import { Card } from "../climate-data/range-selector";

type RainfallDataContextValue = {
  data: RainfallData;
  isFetching: boolean;
};

const RainfallDataContext = createContext<RainfallDataContextValue | null>(
  null
);

export const useRainfallDataContext = () => {
  const ctx = useContext(RainfallDataContext);
  if (!ctx)
    throw new Error(
      "useRainfallDataContext must be used within RainfallDataLoader"
    );
  return ctx;
};

export const RainfallDataLoader = ({
  areaId,
  from,
  to,
  granularity,
  children,
}: {
  areaId: string;
  from: string;
  to: string;
  granularity: ClimateDataGranularity;
  children: React.ReactNode;
}) => {
  const { t } = useTranslation();
  const { data, isLoading, isError, isFetching } = useRainfallData({
    areaId,
    from,
    to,
    granularity,
  });

  if (isError) {
    return (
      <Card tw="flex items-center gap-2 px-4 py-3 text-sm text-red-600">
        <TriangleAlert tw="w-4 h-4" />
        {t("meteo.climateView.loadError")}
      </Card>
    );
  }

  if (isLoading || !data) {
    return (
      <Card tw="flex items-center justify-center gap-2 px-4 py-8 text-sm text-gray-500">
        <Loader2 tw="w-4 h-4 animate-spin" />
        {t("meteo.climateView.loading")}
      </Card>
    );
  }

  return (
    <RainfallDataContext.Provider value={{ data, isFetching }}>
      {children}
    </RainfallDataContext.Provider>
  );
};
